import { Injectable, inject } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { WorkspaceService } from '@ao/shared/services/workspace.service';
import { Observable, map, of, race, timer } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthReadyService } from '@ao/core/services/auth-ready.service';

@Injectable({ providedIn: 'root' })
export class TenantGuard implements CanActivate {
  private workspaceService = inject(WorkspaceService);
  private router = inject(Router);
  private authReadyService = inject(AuthReadyService);

  canActivate(): Observable<boolean | UrlTree> {
    // Wait until AppComponent has confirmed IsLoggedin=true is committed to the
    // DB before calling client/tenants. This prevents the race condition where
    // UserMiddleware blocks the request with 401 because the login-history
    // update hasn't reached the DB yet.
    // A 5-second fallback ensures the guard never blocks the app indefinitely
    // if the login-status API call is slow or fails.
    const ready$ = this.authReadyService.isReady$.pipe(
      filter(ready => ready),
      take(1)
    );
    const timeout$ = timer(5000).pipe(map(() => true));

    return race(ready$, timeout$).pipe(
      take(1),
      switchMap(() =>
        this.workspaceService.getClients().pipe(
          map(tenants =>
            Array.isArray(tenants) && tenants.length > 0
              ? true
              : this.router.parseUrl('/no-auth')
          ),
          catchError(() => of(this.router.parseUrl('/no-auth')))
        )
      )
    );
  }
}
